import { useCallback, useEffect, useState } from 'react';
import {
  getKycDetailEndpoint,
  changeKycStatusEndpoint,
  resendKyclEndpoint,
  generateDocumentEndpoint,
  forceMovePhaseKycEnpoint, // Agrega el import si no existe
} from '../services/kycService';
import useAuth from '../../../hooks/useAuth';
import useCallApiAndLoad from '../../../hooks/useCallApiAndLoad';
import { fetchResponseAdapter } from '../../../adapters/fetchAdapter';
import {
  ChangeKycStatus,
  KycDetailResponse,
  ResendKycForm,
  ReviewForm,
} from '../models/kycModel';
import { useNavigate } from 'react-router-dom';
import { KycStatus } from '../constants/kycConstants';
import {
  ErrorAlert,
  SuccessAlert,
  WarningAlert,
} from '../../../models/alertModel';

export const useKycDetail = (id: string) => {
  const { isLoading, callEndpoint } = useCallApiAndLoad();
  const { getAuthToken } = useAuth();
  const token = getAuthToken();
  const [kycDetail, setKycDetail] = useState<KycDetailResponse | null>(null);
  const [errorDetail, setErrorDetail] = useState<string | null>(null);
  const [resendModalOpen, setResendModalOpen] = useState<boolean>(false);
  const defaultEmptyAlert: SuccessAlert | ErrorAlert | WarningAlert = {
    success: false,
    warning: false,
    danger: false,
    title: '',
    text: '',
    onConfirm: () => {},
    onCancel: () => {},
  };

  const [alertProps, setAlertProps] = useState<
    SuccessAlert | ErrorAlert | WarningAlert
  >(defaultEmptyAlert);

  const navigate = useNavigate();

  const clearAlert = () => {
    setAlertProps(defaultEmptyAlert);
  };

  const loadKycDetail = useCallback(async () => {
    const { status, data } = await callEndpoint(
      getKycDetailEndpoint(id, token),
    );

    const response = fetchResponseAdapter(data);

    if (status === 200) {
      setKycDetail(response.data);
    } else {
      setErrorDetail(response.message);
    }
  }, [callEndpoint, token]);

  const handleCustomerReviewForm = async (form: ReviewForm) => {
    console.log('Enviando formulario de revisión del cliente:', form);
    const dto: ChangeKycStatus = {
      status: KycStatus.NEED_CUSTOMER_REVIEW,
      comment: form.comment,
    };
    const { status, data } = await callEndpoint(
      changeKycStatusEndpoint(id, dto, token),
    );
    const response = fetchResponseAdapter(data);
    if (status === 200) {
      navigate(0);
    } else {
      setAlertProps((state) => ({
        ...state,
        success: false,
        danger: true,
        title: 'Error',
        text: response.message,
        onConfirm: () => {
          clearAlert();
        },
      }));
    }
    // Aquí puedes implementar la lógica para enviar el formulario de revisión del cliente
    // Por ejemplo, llamar a un endpoint de API para guardar el comentario
    // await callEndpoint(sendCustomerReviewEndpoint(form, token));
    // Luego, puedes cerrar el modal y limpiar el formulario si es necesario
  };

  // Acción para el botón "Marcar como completado"
  const handleChangeStatus = async (kycStatus: KycStatus) => {
    console.log(`Cambiando estado de KYC a: ${kycStatus}`);
    const dto: ChangeKycStatus = {
      status: kycStatus,
    };
    const { status, data } = await callEndpoint(
      changeKycStatusEndpoint(id, dto, token),
    );
    const response = fetchResponseAdapter(data);
    if (status === 200) {
      navigate(0);
    } else {
      setAlertProps((state) => ({
        ...state,
        success: false,
        danger: true,
        title: 'Error',
        text: response.message,
        onConfirm: () => {
          clearAlert();
        },
      }));
    }
  };

  // Action for FORCE move KYC for phase 2,
  const handleForceMovePhase = async () => {
    console.log(`Forzando movimiento de KYC a fase 2`);
    const { status, data } = await callEndpoint(
      forceMovePhaseKycEnpoint(id, token),
    );
    const response = fetchResponseAdapter(data);
    if (status === 200) {
      navigate(0);
    } else {
      setAlertProps((state) => ({
        ...state,
        success: false,
        danger: true,
        title: 'Error',
        text: response.message,
        onConfirm: () => {
          clearAlert();
        },
      }));
    }
  };

  const handleSendMail = async () => {
    const form: ResendKycForm = {
      id,
      provider: 'email',
    };
    const { status, data } = await callEndpoint(
      resendKyclEndpoint(form, token),
    );
    const response = fetchResponseAdapter(data);
    if (status === 200 || status === 201) {
      setAlertProps((state) => ({
        ...state,
        success: true,
        danger: false,
        title: 'OK',
        text: 'Correo enviado satisfactoriamente',
        onConfirm: () => {
          clearAlert();
          setResendModalOpen(false);
        },
      }));
    } else {
      setAlertProps((state) => ({
        ...state,
        success: false,
        danger: true,
        title: 'Error',
        text: response.message,
        onConfirm: () => {
          clearAlert();
          setResendModalOpen(false);
        },
      }));
    }
  };

  const handleSendWhatsapp = async () => {
    const form: ResendKycForm = {
      id,
      provider: 'whatsapp',
    };
    const { status, data } = await callEndpoint(
      resendKyclEndpoint(form, token),
    );
    const response = fetchResponseAdapter(data);
    if (status === 200 || status === 201) {
      alert('KYC reenviado por WhatsApp');
      setResendModalOpen(false);
    } else {
      setErrorDetail(response.message);
    }
  };

  const handleGenerateDocument = async () => {
    const { status, data } = await callEndpoint(
      generateDocumentEndpoint(id, token),
    );
    const response = fetchResponseAdapter(data);
    if (status === 200 || status === 201) {
      setAlertProps((state) => ({
        ...state,
        success: true,
        danger: false,
        title: 'Documento generado',
        text: 'El documento se generó correctamente. debe esperar unos minutos a que el documento esté disponible.',
        onConfirm: () => {
          clearAlert();
        },
      }));
    } else {
      setAlertProps((state) => ({
        ...state,
        success: false,
        danger: true,
        title: 'Error',
        text: response.message || 'No se pudo generar el documento.',
        onConfirm: () => {
          clearAlert();
        },
      }));
    }
  };

  useEffect(() => {
    loadKycDetail().catch((e) => setErrorDetail(e.toString()));
  }, []);

  return {
    isLoading,
    kycDetail,
    errorDetail,
    resendModalOpen,
    setResendModalOpen,
    handleChangeStatus,
    handleForceMovePhase,
    handleSendMail,
    handleSendWhatsapp,
    handleCustomerReviewForm,
    handleGenerateDocument,
    // initial states for alert
    defaultEmptyAlert,
    alertProps,
    setAlertProps,
    clearAlert,
  };
};

// ...existing
